import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import roleApi from '../../../api/role/RoleApi'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import LayoutSingleView from '../../common/layout/LayoutSingleView'
import { MenuItems } from '../../../enums/MenuItems'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import RoleCard from '../RoleCard'
import Section from '../../common/sections/Section'
import { CandidateReviewStatus } from '../../../api/role/models/CandidateReviewStatus'
import CandidateReviewResults from './CandidateReviewResults'
import helixSearchV2Api from '../../../api/helixSearchV2/HelixSearchV2Api'
import HelixSearchV2ProgressModalCopy from '../../helixSearchV2Copy/HelixSearchV2ProgressModalCopy'

export default function CandidateReview(): React.JSX.Element {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const roleId = useMemo(() => searchParams.get('id'), [searchParams])
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState<string | undefined>()
    const [roleLite, setRoleLite] = useState<any>(undefined)
    const [searchSessionToken, setSearchSessionToken] = useState<string | null>(
        null,
    )
    const [searchLogId, setSearchLogId] = useState<string | null>(null)
    const [showSearchProgress, setShowSearchProgress] = useState(false)
    const [showResults, setShowResults] = useState(false)
    const initializedRoleIdRef = useRef<string | null>(null)

    const handleSearchComplete = useCallback(async () => {
        setShowSearchProgress(false)

        let latestStatus: Awaited<
            ReturnType<typeof helixSearchV2Api.getSearchStatus>
        > | null = null

        if (searchSessionToken) {
            try {
                latestStatus = await helixSearchV2Api.getSearchStatus({
                    sst: searchSessionToken,
                })
                setSearchLogId(latestStatus.logId)
                if (latestStatus.sessionStatus === 'COMPLETE') {
                    setShowResults(true)
                }
            } catch (error) {
                console.error('Failed to refresh Helix search status', error)
            }
        }

        // Check if candidateReviewStatus is NOT_STARTED and update to IN_PROGRESS if needed
        if (roleId) {
            try {
                const statusResponse = await roleApi.getCandidateReviewStatus({
                    roleId,
                })

                if (
                    statusResponse.candidateReviewStatus ===
                    CandidateReviewStatus.NOT_STARTED
                ) {
                    await roleApi.updateCandidateReviewStatus({
                        roleId,
                        candidateReviewStatus:
                            CandidateReviewStatus.IN_PROGRESS,
                    })
                }
            } catch (error) {
                console.error('Error updating candidate review status:', error)
                setErrMsg('Failed to update candidate review status.')
            }
        }

        if (
            latestStatus &&
            latestStatus.sessionStatus !== 'COMPLETE' &&
            latestStatus.sessionStatus !== 'FAILED'
        ) {
            setErrMsg('Candidate search did not complete successfully.')
        }
    }, [roleId, searchSessionToken])

    useEffect(() => {
        if (!showSearchProgress || !searchSessionToken) {
            return
        }

        let isActive = true
        let timeoutId: number | null = null

        const pollStatus = async () => {
            if (!isActive) {
                return
            }

            try {
                const status = await helixSearchV2Api.getSearchStatus({
                    sst: searchSessionToken,
                })

                if (!isActive) {
                    return
                }

                if (status.sessionStatus === 'COMPLETE') {
                    await handleSearchComplete()
                    return
                }

                if (
                    status.sessionStatus === 'FAILED' ||
                    status.sessionStatus === 'CANCELED' ||
                    status.sessionStatus === 'EXPIRED'
                ) {
                    console.error(
                        'Candidate review search ended before completing.',
                        status,
                    )
                    setErrMsg('Candidate search did not complete successfully.')
                    setShowSearchProgress(false)
                    return
                }
            } catch (error) {
                console.error('Failed to poll Helix search status', error)
            }

            timeoutId = window.setTimeout(pollStatus, 2000)
        }

        pollStatus()

        return () => {
            isActive = false
            if (timeoutId) {
                window.clearTimeout(timeoutId)
            }
        }
    }, [handleSearchComplete, searchSessionToken, showSearchProgress])

    useEffect(() => {
        if (!roleId) {
            return
        }
        if (initializedRoleIdRef.current === roleId) {
            return
        }
        initializedRoleIdRef.current = roleId

        const fetchData = async () => {
            try {
                setErrMsg(undefined)
                setIsLoading(true)

                const { role } = await roleApi.getLite({
                    roleId,
                    includeHiringTeamIds: true,
                })
                setRoleLite(role)

                // Get or start candidate review search
                const searchSessionTokenResponse =
                    await roleApi.getOrStartCandidateReviewSearch({
                        roleId,
                    })

                setSearchSessionToken(
                    searchSessionTokenResponse.searchSessionToken,
                )
                setSearchLogId(searchSessionTokenResponse.logId)

                const status = await helixSearchV2Api.getSearchStatus({
                    sst: searchSessionTokenResponse.searchSessionToken,
                })

                setSearchLogId(status.logId)

                if (status.sessionStatus !== 'COMPLETE') {
                    setShowResults(false)
                    setShowSearchProgress(true)
                } else {
                    setShowSearchProgress(false)
                    setShowResults(true)
                }
            } catch (error) {
                console.error(error)
                if (
                    error instanceof Error &&
                    error.message.includes('Role not found')
                ) {
                    navigate('/roles', { replace: true })
                    return
                }
                const msg =
                    'There was a problem loading the role. Please refresh the page to try again. Error details: '
                if (error instanceof Error) {
                    setErrMsg(`${msg}${error.message}`)
                } else {
                    // Handle non-Error objects (e.g., strings or custom error shapes)
                    setErrMsg(`${msg}An unknown error occurred.`)
                }
            } finally {
                setIsLoading(false)
            }
        }
        fetchData()
    }, [navigate, roleId])

    if (errMsg) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Section title="Candidate Review" titleType="h2">
                    <div className="mt-2">
                        <Note type={NoteType.ERROR}>{errMsg}</Note>
                    </div>
                </Section>
            </LayoutSingleView>
        )
    }

    if (isLoading || !roleLite) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Section title="Candidate Review" titleType="h2">
                    <div className="mt-2">
                        <LoadingSpinner />
                    </div>
                </Section>
            </LayoutSingleView>
        )
    }

    return (
        <LayoutSingleView activeMenuItem={MenuItems.Roles}>
            <Section title="Candidate Review" titleType="h2">
                <div className="mt-2">
                    <RoleCard
                        role={roleLite}
                        showJobDescription={true}
                        clickable={false}
                    />
                </div>
                {showResults && searchSessionToken && searchLogId && roleId && (
                    <div className="mt-2">
                        <CandidateReviewResults
                            sst={searchSessionToken}
                            logId={searchLogId}
                            roleId={roleId}
                        />
                    </div>
                )}
            </Section>

            {showSearchProgress && searchSessionToken && searchLogId && (
                <HelixSearchV2ProgressModalCopy
                    sst={searchSessionToken}
                    logId={searchLogId}
                    onClose={handleSearchComplete}
                    resultsLoaded={showResults}
                />
            )}
        </LayoutSingleView>
    )
}
